import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Cookie from '@wetransfer/concorde-cookie';

import { PORCELAIN, DODGER_BLUE } from '@constant/colors';

const Spinner = ({
  background,
  children,
  circumference,
  className,
  hideText,
  foreground,
  labelClassName,
  onClick,
  percentage,
  rotate,
  stroke,
}) => {
  percentage = Math.max(0, Math.min(100, Math.floor(percentage)));

  // Build the className
  const spinnerClassName = ['spinner'];
  if (className) {
    spinnerClassName.push(className);
  }

  // Only allow spinning if the prop is true and the cookie is not set
  const spinnerCookie = !!Cookie.get('wt_nospinner');
  if (rotate && !spinnerCookie) {
    spinnerClassName.push('spinner--rotate');
  }

  // We used these classNames to adjust positioning slightly depending
  // on the number of digits that's on screen and to determine if we need
  // to display rotation or not.
  if (percentage < 10) {
    spinnerClassName.push('spinner--single');
  } else if (percentage < 100) {
    spinnerClassName.push('spinner--double');
  } else {
    spinnerClassName.push('spinner--triple');
  }

  // Some basic math, we'll need these a few times later.
  const actualRadius = circumference / 2;
  const strokeHalf = stroke / 2;

  // The radius we'll use for math actually gets rid of the
  // stroke size too because otherwise it will be drawn partially
  // outside of view.
  const mathRadius = actualRadius - strokeHalf;
  const center = Math.PI * (mathRadius * 2);
  const strokePercentage = Math.max(0.5, percentage); // For drawing we don't want to show 0 percentage
  const strokeOffset = ((100 - strokePercentage) / 100) * center;
  const pathLength = Math.PI * 2 * mathRadius;

  // Export our simple SVG element
  return (
    <div className={spinnerClassName.join(' ')} onClick={onClick}>
      <svg
        height={circumference}
        width={circumference}
        className="spinner__circle"
        shapeRendering="geometricPrecision"
        viewBox={`0 0 ${circumference} ${circumference}`}
      >
        <circle
          className="spinner__background"
          r={actualRadius - strokeHalf}
          cx={actualRadius}
          cy={actualRadius}
          fill="transparent"
          style={{
            strokeDasharray: pathLength,
            strokeDashoffset: 0,
            strokeWidth: stroke,
            stroke: background,
          }}
        />

        <circle
          className="spinner__foreground"
          r={actualRadius - strokeHalf}
          cx={actualRadius}
          cy={actualRadius}
          fill="transparent"
          style={{
            strokeDasharray: pathLength,
            strokeDashoffset: strokeOffset,
            strokeWidth: stroke,
            stroke: foreground,
          }}
        />
      </svg>
      {!hideText && (
        <span className={classNames('spinner__label', labelClassName)}>
          {percentage}
          <span>%</span>
        </span>
      )}
      {children}
    </div>
  );
};

Spinner.propTypes = {
  background: PropTypes.string.isRequired,
  children: PropTypes.node,
  circumference: PropTypes.number.isRequired,
  className: PropTypes.string,
  foreground: PropTypes.string.isRequired,
  hideText: PropTypes.bool,
  labelClassName: PropTypes.string,
  onClick: PropTypes.func,
  percentage: PropTypes.number.isRequired,
  rotate: PropTypes.bool.isRequired,
  stroke: PropTypes.number.isRequired,
};

Spinner.defaultProps = {
  background: PORCELAIN,
  circumference: 170,
  foreground: DODGER_BLUE,
  percentage: 0,
  rotate: false,
  stroke: 10,
};

export default Spinner;
